import './MyBlockMonthHeader.scss';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FormControl, MenuItem, Select } from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import ReplayIcon from '@mui/icons-material/Replay';
import { blockType } from '../../constants/myBlockMonth';
import { useMyBlockMonthProvider } from '../../providers/MyBlockMonthProvider';

const useStyles = makeStyles(theme => ({
    menuPaper: {
      border: '1px solid #e2e2e2',
      maxHeight: '20.1rem',
      marginTop: '0.6rem',
      '&& .Mui-selected': {
        backgroundColor: '#f9f9f9'
      },
      '&& .MuiList-padding': {
        padding: '0'
      }   
    },
    menuItem: {
        borderBottom: 'solid 1px #eeeeee',
        color: '#4b4f54',
        marginRight: '0.5rem',
        minWidth: '22.7rem',
        paddingBottom: '1.5rem',
        paddingTop: '1rem'
    }
}));


const MyBlockMonthHeader = ({searchDate, setSearchDate, setDatesDropDownIndex, datesDropDownIndex, noData}) => {
    const { t } = useTranslation(); 
    const myBlockMonthContext = useMyBlockMonthProvider();

    const [cssClasses, setCssClasses] = useState({
        iconContainer: 'icon-cntr',
        icon: 'icon-chevron-thin-down',
        select: 'select-dropdown'
    });
    const [dateValue, setDateValue] = useState('');
    const [dates, setDates] = useState([] as any);
    const [open, setOpen] = useState(false);
    const [renderedDate, setRenderedDate] = useState('');
    const blockMonthType = myBlockMonthContext.myBlockMonthData?.blockList?.BLOCK_TYPE_MNT ?? "N/A";

    const classes = useStyles(); 
    const menuProps: any = {
        anchorOrigin: {
            vertical: 'bottom',
            horizontal: 'left'
        },
        transformOrigin: {
            vertical: 'top',
            horizontal: 'left'
        },
        getContentAnchorEl: null,
        classes : {
            paper: classes.menuPaper
        }
    };
    const months = [ 
        {short: 'JAN', long: 'January'}, 
        {short: 'FEB', long: 'February'}, 
        {short: 'MAR', long: 'March'},
        {short: 'APR', long: 'April'}, 
        {short: 'MAY', long: 'May'}, 
        {short: 'JUN', long: 'June'},
        {short: 'JUL', long: 'July'}, 
        {short: 'AUG', long: 'August'},
        {short: 'SEP', long: 'September'},
        {short: 'OCT', long: 'October'},
        {short: 'NOV', long: 'November'},
        {short: 'DEC', long: 'December'},
    ];

    useEffect(() => {
        setDates(myBlockMonthContext.myBlockMonthDates.map(dates => {
            return {
                monthNumber: months.findIndex(month => month.long === dates.BID_PERIOD_MNTH)+1,
                year: dates.BID_PERIOD_YR,
                fullDate: `${dates.BID_PERIOD_MNTH} ${dates.BID_PERIOD_YR}`
            }
        }))

    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])
    
    useEffect(() => {
        if(dates.length === 0) return;
        setDateValue(dates.findIndex(date => date.monthNumber === searchDate.month && date.year === searchDate.year));
        setRenderedDate(transformDate(`${months[searchDate.month-1].long} ${searchDate.year}`));

    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [dates])
    
    const handleChange = (event) => {
        setSearchDate({
            month: dates[event.target.value].monthNumber,
            year: dates[event.target.value].year
        })
        setDatesDropDownIndex(event.target.value);
    }

    const handleOpenClose = () => {
        if(open) {
            setCssClasses({
                iconContainer: 'icon-cntr',
                icon: 'icon-chevron-thin-down',
                select: 'select-dropdown'
            })
        } else {
            setCssClasses({
                iconContainer: 'icon-cntr inverted',
                icon: 'icon-chevron-thin-down selected',
                select: 'select-dropdown opened'
            })
        }
        setOpen(!open);
    }

    const iconComponent = () => {
        return (
            <div onClick={handleOpenClose} className={cssClasses.iconContainer}>
                <i className={cssClasses.icon} />
            </div>
        )
    }

    const renderValue = () => {
        return <span>{renderedDate}</span>;
    }

    /**)
     * @param date 
     * @returns transformed date (example: February 2022 -> February '22)
     */
    const transformDate = (date) => {
        return `${date.substring(0,date.indexOf(' '))} '${date.substring(date.length-2)}`;
    }

    return (
        <div className="c-mbm-header header-grey">
            <div className="c-sub-header">
                <div className="c-title">
                    <h1>{t('myBlockMonth.title')} for </h1>
                    <FormControl variant="standard">
                        <Select
                            className={cssClasses.select}
                            disableUnderline
                            IconComponent={iconComponent}
                            MenuProps={menuProps}
                            onChange={handleChange}
                            onClose={handleOpenClose}
                            onOpen={handleOpenClose}
                            open={open}
                            renderValue={renderValue}
                            value={dateValue}
                        >
                            {dates.map((date,index) => {
                                return (
                                    <MenuItem 
                                        className={classes.menuItem} 
                                        style={{ fontSize: '1.6rem' }} 
                                        value={index}
                                        key={index}
                                    >
                                        {date.fullDate}
                                    </MenuItem>
                                )
                            })}
                        </Select>
                    </FormControl>
                </div>
                {!noData && datesDropDownIndex === 0 && (
                    <span className="updated-time">{t('myBlockMonth.lastUpdated')}: {myBlockMonthContext?.myBlockMonthData?.timeInEST ?? ''} ET</span>
                )}
            </div>
            <div className="c-sub-header">
                <div className="sub-title">
                    <span>{myBlockMonthContext?.myBlockMonthData?.blockList?.baseLocation ?? 'N/A'}</span>
                    <span>{myBlockMonthContext?.myBlockMonthData?.blockList?.equipmentCode ?? 'N/A'}</span>
                    <span>{myBlockMonthContext?.myBlockMonthData?.blockList?.position ?? 'N/A'}</span>
                    <span>{t('myBlockMonth.mySeniority')} # {myBlockMonthContext?.myBlockMonthData?.blockList?.seniority ?? 'N/A'}</span>
                    <span> 
                        {blockMonthType === blockType.RESERVE 
                            ? t('myBlockMonth.reserve') 
                            : blockMonthType === blockType.NONBIDDER 
                                ? t('myBlockMonth.nonBidder') 
                                : t('myBlockMonth.blockHolder')} # {myBlockMonthContext?.myBlockMonthData?.blockHolder ?? "N/A"}
                    </span>
                </div>
                {datesDropDownIndex === 0 && (
                    <div className="reset-button-container" onClick={() => window.location.reload()}>
                        <span className="reset-button-link no-padding">Refresh</span>
                        <ReplayIcon viewBox="-4,0,24,24" fontSize="large" className="replay-con" />
                    </div> 
                )}
            </div>
        </div>
    );
}

export default MyBlockMonthHeader;